import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Linkify from "@/components/ui/linkify";
import { getPickupBanner } from "@/api/settings";

/**
 * Фолбэк на случай, если настройки недоступны (например, бэкенд не ответил).
 * Актуальный текст живёт в настройках сайта (админка → Настройки).
 */
const FALLBACK_TITLE = "Как забрать заказ?";
const FALLBACK_TEXT = `ОПЛАТА НАЛИЧНЫМИ ПРИ ПОЛУЧЕНИИ

🩷 Забрать заказ можно на Васильева 3.

‼️ПРИМЕРКИ НЕТ‼️
Можно примерить дома и вернуть в течении 2-4 дней

Второй этаж, где ремонт телефонов.

🩷 БУДНИ — с 12:00 до 18.30 ч.
🩷 СУББОТА — с 12:00 до 15:00 ч.

Об изменениях буду сообщать в группе в телеграмм и в вк.`;

/** Строка целиком ЗАГЛАВНЫМИ — это заголовок, показываем жирным. */
function isHeading(line: string): boolean {
  const letters = line.replace(/[^\p{L}]/gu, "");
  return (
    letters.length > 0 &&
    letters === letters.toUpperCase() &&
    letters !== letters.toLowerCase()
  );
}

/** Текст плашки → абзацы (разделитель — пустая строка) со строками внутри. */
function toParagraphs(text: string): string[][] {
  return text
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/)
    .map((block) => block.split("\n").filter((line) => line.trim() !== ""))
    .filter((lines) => lines.length > 0);
}

export default function PickupInfoBanner() {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [banner, setBanner] = useState<{ title: string; text: string } | null>(
    null,
  );

  const inCatalog = pathname.startsWith("/catalog");

  useEffect(() => {
    if (!inCatalog) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getPickupBanner();
        if (!cancelled) setBanner(data);
      } catch {
        if (!cancelled)
          setBanner({ title: FALLBACK_TITLE, text: FALLBACK_TEXT });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inCatalog]);

  // Показываем только в каталоге (список товаров и страница товара)
  if (!inCatalog) {
    return null;
  }

  // Ещё не загрузили или админ очистил текст — плашки нет
  if (!banner || !banner.text.trim()) {
    return null;
  }

  const title = banner.title.trim() || FALLBACK_TITLE;
  const paragraphs = toParagraphs(banner.text);

  return (
    <div data-testid="pickup-info-banner" className="border-b border-green-700/20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full bg-green-600 hover:bg-green-700 transition-colors text-white px-4 py-3"
        aria-expanded={expanded}
        data-testid="pickup-info-toggle"
      >
        <span className="flex items-center justify-center gap-2">
          <span className="font-bold text-base sm:text-lg">{title}</span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>

      {expanded && (
        <div
          className="bg-green-50 text-green-900 px-4 py-4 text-xs sm:text-sm leading-relaxed"
          data-testid="pickup-info-body"
        >
          <div className="mx-auto max-w-3xl space-y-3">
            {paragraphs.map((lines, blockIdx) => (
              <div key={blockIdx} className="space-y-0.5">
                {lines.map((line, lineIdx) => (
                  <p
                    key={lineIdx}
                    className={isHeading(line) ? "font-bold" : undefined}
                  >
                    <Linkify text={line} />
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
